type Listener<Payload> = Payload extends void
  ? () => void
  : (payload: Payload) => void;

export class EventBus<EM extends Record<string, any>> {
  on<K extends keyof EM>(type: K, listener: Listener<EM[K]>): () => void {
    const wrapped = (e: Event) => listener((e as CustomEvent<EM[K]>).detail);
    document.addEventListener(type as string, wrapped);
    return () => document.removeEventListener(type as string, wrapped);
  }

  off<K extends keyof EM>(type: K, listener: Listener<EM[K]>): void {
    // Note: this only works if you saved the wrapped fn reference
    // (for a real implementation you might store them in a Map)
    document.removeEventListener(type as string, listener as any);
  }

  emit<K extends keyof EM>(type: K, payload: EM[K] = undefined): void {
    // bubbles: true so external page code listening on window receive the event
    const event = new CustomEvent(type as string, {
      detail: payload,
      bubbles: true,
    });
    document.dispatchEvent(event);
  }

  /** Emit and push to GTM’s dataLayer under `eventName` (defaults to the same as `type`) */
  emitAndTrack<K extends keyof EM>(
    type: K,
    payload: EM[K],
    eventName?: string,
  ): void {
    this.emit(type, payload);

    // @ts-ignore — window.dataLayer is globally injected by GTM snippet
    window.dataLayer = window.dataLayer || [];
    // Merge in payload so GTM can pick up custom dimensions
    window['dataLayer'].push({
      event: eventName ?? (type as string),
      ...((payload as object) || {}),
    });
  }
}
